import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useHoldedIntegracion, useHoldedSyncPendientes } from '@/hooks/useHoldedIntegracion';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Global "Sincronizar todo" button shown only when integration is active and user is admin.
 * Color: outline (ok), yellow (pendientes), destructive (errors).
 */
export function HoldedSyncAllButton({ onDone }: { onDone?: () => void }) {
  const { activa } = useHoldedIntegracion();
  const { hasRole } = useAuth();
  const { data: pend, refetch } = useHoldedSyncPendientes();
  const [busy, setBusy] = useState(false);

  const extractErrorMessage = async (error: any, data: any): Promise<string | null> => {
    if (data && (data as any).error) return (data as any).error as string;
    if (!error) return null;
    try {
      const ctx = error.context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) return body.error as string;
      } else if (ctx && typeof ctx.text === 'function') {
        const txt = await ctx.text();
        if (txt) return txt;
      }
    } catch (_) { /* fallthrough */ }
    return error.message || 'Error desconocido';
  };

  if (!activa || !(hasRole('admin') || hasRole('super_admin'))) return null;

  const errores = pend?.errores ?? 0;
  const pendientes = pend?.pendientes ?? 0;
  const hayErrores = errores > 0;
  const hayPendientes = pendientes > 0;

  const run = async () => {
    if (!confirm(`Sincronizar todos los clientes con Holded? Esto puede tardar unos segundos.`)) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('holded-sync-all', { body: {} });
      const msg = await extractErrorMessage(error, data);
      if (msg) throw new Error(msg);
      const r = data as any;
      toast.success(`Sync: ${r.creados} creados, ${r.actualizados} actualizados, ${r.errores} errores`);
      refetch();
      onDone?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const label = hayErrores
    ? `Revisar errores (${errores})`
    : hayPendientes
    ? `Sincronizar (${pendientes} pendientes)`
    : 'Sincronizado';

  const className = hayErrores
    ? ''
    : hayPendientes
    ? 'bg-yellow-500 hover:bg-yellow-600 text-black border-yellow-600'
    : '';

  const variant: any = hayErrores ? 'destructive' : hayPendientes ? 'default' : 'outline';

  const Icon = hayErrores ? AlertCircle : hayPendientes ? RefreshCw : CheckCircle2;

  return (
    <Button onClick={run} disabled={busy} variant={variant} className={className}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Icon className="h-4 w-4 mr-2" />}
      {label}
    </Button>
  );
}

/**
 * Per-cliente badge + refresh button (used in client detail).
 */
export function HoldedClienteSyncBadge({ cliente, onSynced }: { cliente: any; onSynced?: () => void }) {
  const { activa } = useHoldedIntegracion();
  const [busy, setBusy] = useState(false);

  const extractErrorMessage = async (error: any, data: any): Promise<string | null> => {
    if (data && (data as any).error) return (data as any).error as string;
    if (!error) return null;
    try {
      const ctx = error.context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) return body.error as string;
      } else if (ctx && typeof ctx.text === 'function') {
        const txt = await ctx.text();
        if (txt) return txt;
      }
    } catch (_) { /* fallthrough */ }
    return error.message || 'Error desconocido';
  };

  if (!activa) return null;

  const estado = cliente?.holded_sync_estado;
  const linked = !!cliente?.holded_contact_id;

  const refresh = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('holded-sync-cliente', {
        body: { clienteId: cliente.id, accion: 'refresh' },
      });
      const msg = await extractErrorMessage(error, data);
      if (msg) throw new Error(msg);
      toast.success('Cliente actualizado desde Holded');
      onSynced?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('holded-sync-cliente', {
        body: { clienteId: cliente.id, accion: linked ? 'update' : 'create' },
      });
      const msg = await extractErrorMessage(error, data);
      if (msg) throw new Error(msg);
      toast.success('Enviado a Holded');
      onSynced?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  let color = 'bg-gray-200 text-gray-700';
  let label = 'Solo Raven';
  if (estado === 'ok' && linked) { color = 'bg-green-100 text-green-800'; label = 'Sincronizado Holded'; }
  else if (estado === 'error') { color = 'bg-red-100 text-red-800'; label = 'Error Holded'; }
  else if (!linked || estado === 'pendiente') { color = 'bg-yellow-100 text-yellow-900'; label = 'Pendiente Holded'; }

  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs px-2 py-1 rounded ${color}`}>{label}</span>
      {linked ? (
        <Button size="sm" variant="ghost" onClick={refresh} disabled={busy}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={resync} disabled={busy}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Enviar a Holded
        </Button>
      )}
    </div>
  );
}
